import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import 'react-quill/dist/quill.snow.css';
import { prepareContentRichPreviewHtml } from '../../utils/contentRichPreviewHtml';

export interface ContentRichPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Raw editor HTML from Quill (or HTML string from server); not Delta JSON for display. */
  html: string;
  title?: string;
}

const ContentRichPreviewDialog: React.FC<ContentRichPreviewDialogProps> = ({
  open,
  onClose,
  html,
  title = 'Content preview',
}) => {
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    if (!open) setFullScreen(false);
  }, [open]);

  const selectedLanguage = typeof localStorage !== 'undefined'
    ? localStorage.getItem('defaultSpeechLanguage') || 'en-US'
    : 'en-US';
  const isRtlLanguage = /^(he|ar|fa|ur)(-|$)/i.test(selectedLanguage);

  const safeHtml = prepareContentRichPreviewHtml(html);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: fullScreen
          ? { display: 'flex', flexDirection: 'column', height: '100%', m: 0, maxHeight: '100%' }
          : {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'min(92vh, 920px)',
              width: '100%',
              m: { xs: 1, sm: 2 },
            },
      }}
    >
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            {title}
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => setFullScreen((v) => !v)}
            aria-label={fullScreen ? 'Exit full screen' : 'Full screen'}
          >
            {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: { xs: 2, sm: 3 },
          bgcolor: 'grey.100',
          minHeight: 0,
        }}
      >
        <Box
          className="ql-snow"
          sx={{
            maxWidth: 'min(1100px, 100%)',
            mx: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
            '& .ql-container.ql-snow': { border: 'none' },
            '& .ql-editor': {
              minHeight: fullScreen ? '70vh' : 'min(52vh, 420px)',
              fontSize: '1.125rem',
              lineHeight: 1.65,
              direction: isRtlLanguage ? 'rtl' : 'ltr',
              textAlign: isRtlLanguage ? 'right' : 'left',
              p: { xs: 2, sm: 4 },
            },
            '& .ql-editor img, & .ql-editor video, & .ql-editor audio': {
              maxWidth: '100%',
              height: 'auto',
            },
          }}
        >
          <Box className="ql-container ql-snow" sx={{ border: 'none' }}>
            <Box
              component="div"
              className="ql-editor"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ContentRichPreviewDialog;
